import {
  IsEmail,
  IsEnum,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  MaxLength,
  Min,
  MinLength,
  IsBoolean,
  ValidateIf
} from "class-validator";
import {
  JobTitlesMarketing,
  MainPropertyTypeEnum,
  OtherJobTitles,
  WhenPlanToUseSoftware,
  YesNoEnum
} from "./marketing";
import moment from "moment";

export enum MarketingCampaign {
  GoogleSearch = "GoogleSearch",
  YouTube = "YouTube",
  Instagram = "Instagram",
  TikTok = "TikTok",
  Facebook = "Facebook",
  Webinar = "Webinar",
  Podcast = "Podcast",
  LinkedIn = "LinkedIn",
  Reddit = "Reddit",
  Quora = "Quora",
  Referral = "Referral",
  Other = "Other"
}

export const isntNorthAmerica = (country?: string) =>
  country ? !["Canada", "United States"].includes(country) : false;

export enum DoorLoopLeadSources {
  FREE_TRIAL = "Trial Signup",
  SCHEDULE_DEMO = "Scheduled Demo",
  SCHEDULE_CALL = "Scheduled Call",
  BLOG_SUBSCRIBER = "Blog Subscriber",
  AWAITING_CALLBACK = "Awaiting Callback"
}

export enum MarketingCampaignNotSelectable {
  GoogleAds = "google-ads",
  Branded = "branded",
  UtmSourceCookieParseFailed = "dlmc cookie parse failed",
  UtmSourceCookieNotFound = "dlmc cookie not found"
}

const PHONE_MESSAGE_ERROR = "Please enter a valid phone number";

export class DoorLoopMarketingInfoDto {
  fillDataFromRequest(req: any) {
    if (!req.cookies) {
      this.utmSource = "req.cookies undefined";
      return;
    }
    this.fillDataFromCookies(req.cookies.dlmc, req.cookies._ga);
  }

  fillDataFromCookies(dlmcCookie?: string, gaClientId?: string) {
    if (gaClientId && gaClientId.length > 6) {
      this.gaTrackId = process.env.GA_MEASUREMENT_ID;
      this.gaClientId = gaClientId.substring(6, gaClientId.length);
    } else {
      this.gaClientId = "_ga cookie not found";
    }
    if (dlmcCookie) {
      const cookieObject = this.parseCookie(dlmcCookie);
      if (cookieObject) {
        this.referrer = cookieObject.referrer;
        this.landingPage = cookieObject.landingPage;
        this.utmTerm = cookieObject.utmTerm;
        this.utmCampaign = cookieObject.utmCampaign;
        this.utmSource = cookieObject.utmSource;
        this.utmMedium = cookieObject.utmMedium;
        this.utmContent = cookieObject.utmContent;
        this.adwordsGclid = cookieObject.adwordsGclid;
        this.firstWebsiteVisitAt = cookieObject.firstWebsiteVisitAt
          ? moment(cookieObject.firstWebsiteVisitAt).toDate()
          : undefined;
      } else {
        this.utmSource = "dlmc cookie parse failed";
      }
    } else {
      this.utmSource = "dlmc cookie not found";
    }
  }

  private parseCookie(cookie?: string) {
    try {
      if (cookie) {
        const value = JSON.parse(decodeURIComponent(cookie));
        return value;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  utmSource?: string;
  utmCampaign?: string;
  utmMedium?: string;
  utmTerm?: string;
  utmContent?: string;
  landingPage?: string;
  adwordsGclid?: string;
  gaClientId?: string;
  gaTrackId?: string;
  referrer?: string;
  firstWebsiteVisitAt?: Date;
  segmentAnonymousId?: string;
}

export class DoorLoopBaseLeadDto {
  @IsEmail()
  @IsNotEmpty()
  email?: string;

  @IsOptional()
  @IsEnum(DoorLoopLeadSources)
  leadSource?: DoorLoopLeadSources;

  @IsNotEmpty({ message: PHONE_MESSAGE_ERROR })
  @MinLength(7, { message: PHONE_MESSAGE_ERROR })
  @MaxLength(30, { message: PHONE_MESSAGE_ERROR })
  phone?: string;

  @IsString()
  @MaxLength(200)
  name?: string;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  companyName?: string;

  @IsNumber({ maxDecimalPlaces: 0 })
  @Min(1, { message: "Number of units must be at least 1" })
  numberOfUnits?: number;
}

export class DoorLoopLeadDto extends DoorLoopBaseLeadDto {
  @IsOptional()
  @IsString()
  sfLeadId?: string;

  @IsOptional()
  @IsBoolean()
  skipEmailVerification?: boolean;

  @IsOptional()
  @IsBoolean()
  isUsingVPN?: boolean;

  @IsOptional()
  @IsString()
  @MaxLength(100)
  description?: string;

  @IsOptional()
  marketingInfo?: DoorLoopMarketingInfoDto;

  @IsOptional()
  @IsEnum({ ...MarketingCampaign, ...MarketingCampaignNotSelectable })
  source?: MarketingCampaign | MarketingCampaignNotSelectable;

  @ValidateIf((x) => x.source === MarketingCampaign.Other)
  @IsString()
  sourceOther?: string;

  @IsNotEmpty()
  @IsEnum(JobTitlesMarketing)
  selectedJobTitle?: JobTitlesMarketing;

  @ValidateIf((dto) => dto.selectedJobTitle === JobTitlesMarketing.OTHER)
  @IsNotEmpty()
  @IsEnum(OtherJobTitles)
  jobTitle?: OtherJobTitles;

  @IsNotEmpty()
  @IsEnum(MainPropertyTypeEnum)
  mainPropertyType?: MainPropertyTypeEnum;

  @IsNotEmpty()
  @IsEnum(WhenPlanToUseSoftware)
  whenPlanToUseSoftware?: WhenPlanToUseSoftware;

  @ValidateIf((dto) => isntNorthAmerica(dto.country))
  @IsNotEmpty()
  @IsEnum(YesNoEnum)
  doYouManagePropertiesInTheUSOrCanada?: YesNoEnum;

  @IsString()
  @IsOptional()
  medium?: string;

  @IsString()
  @IsOptional()
  campaign?: string;

  @MaxLength(5)
  @IsOptional()
  language?: string;

  @MaxLength(100)
  @IsOptional()
  timezone?: string;

  country?: string;
  ipAddress?: string;
  posthog_distinct_id?: string;

  @IsOptional()
  @IsString()
  utm_source?: string;

  @IsOptional()
  @IsString()
  utm_medium?: string;

  @IsOptional()
  @IsString()
  utm_campaign?: string;

  @IsOptional()
  @IsString()
  utm_content?: string;

  @IsOptional()
  @IsString()
  utm_term?: string;
}
